
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Loader2, AlertCircle, Upload, BarChart3, FileText, MessageSquare } from 'lucide-react';
import { useOpenAIService } from '@/hooks/useOpenAIService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const PredictiveModelingAI = () => {
  const [industry, setIndustry] = useState('');
  const [historicalData, setHistoricalData] = useState('');
  const [availableDatasets, setAvailableDatasets] = useState<any[]>([]);
  const [selectedDataset, setSelectedDataset] = useState('');
  const [naturalLanguageQuery, setNaturalLanguageQuery] = useState('');
  const [predictions, setPredictions] = useState<any[]>([]);
  const [visualizations, setVisualizations] = useState<any[]>([]);
  const [error, setError] = useState<string>('');
  const { callAIService, isLoading, response } = useOpenAIService();

  // Load available cleaned datasets
  useEffect(() => {
    loadAvailableDatasets();
  }, []);

  const loadAvailableDatasets = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('file_storage')
        .select('*')
        .eq('user_id', user.id)
        .or('folder_path.eq./cleaned-data/,folder_path.eq./cleaned-datasets/')
        .eq('is_folder', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAvailableDatasets(data || []);
    } catch (error) {
      console.error('Error loading datasets:', error);
    }
  };

  const loadDatasetContent = async (dataset: any) => {
    try {
      const { data, error } = await supabase.storage
        .from('user_files')
        .download(dataset.file_path);

      if (error) throw error;

      const text = await data.text();
      setHistoricalData(text);
      setSelectedDataset(dataset.file_name);
      toast.success(`Loaded dataset: ${dataset.file_name}`);
    } catch (error) {
      console.error('Error loading dataset content:', error);
      toast.error('Failed to load dataset');
    }
  };

  const handleNaturalLanguageQuery = async () => {
    if (!naturalLanguageQuery.trim() || !historicalData.trim()) {
      toast.error('Please provide both a query and data');
      return;
    }

    setError('');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('Please sign in to use natural language queries');
        return;
      }

      console.log('Processing NL query:', naturalLanguageQuery);

      const answer = await callAIService({
        feature: 'natural_language_query',
        query: naturalLanguageQuery,
        data: historicalData,
        industry: industry || 'general',
        userId: user.id,
        interactionType: 'natural_language_query'
      });

      setPredictions(prev => [...prev, {
        id: Date.now(),
        type: 'query',
        query: naturalLanguageQuery,
        result: answer,
        timestamp: new Date()
      }]);

      setNaturalLanguageQuery('');
      toast.success('Query processed successfully!');
    } catch (error) {
      console.error('Failed to process query:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to process query';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleGeneratePredictions = async () => {
    if (!industry || !historicalData.trim()) {
      toast.error('Please provide both industry and historical data');
      return;
    }

    setError('');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('Please sign in to use predictive modeling');
        return;
      }

      console.log('Generating predictions for:', { industry, dataLength: historicalData.length });

      const predictionResult = await callAIService({
        feature: 'predictive_modeling',
        data: historicalData,
        industry,
        userId: user.id,
        interactionType: 'predictive_modeling'
      });

      setPredictions(prev => [...prev, {
        id: Date.now(),
        type: 'prediction',
        result: predictionResult,
        industry,
        timestamp: new Date()
      }]);

      // Generate simple visualization data
      setVisualizations(prev => [...prev, {
        id: Date.now(),
        type: 'trend',
        title: `${industry} Predictions`,
        data: generateSampleChartData(),
        timestamp: new Date()
      }]);

      console.log('Predictions generated successfully:', predictionResult);
      toast.success('Predictions generated successfully!');
    } catch (error) {
      console.error('Failed to generate predictions:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate predictions';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const generateSampleChartData = () => {
    return Array.from({ length: 12 }, (_, i) => ({
      month: `Month ${i + 1}`,
      predicted: Math.floor(Math.random() * 1000) + 500,
      confidence: Math.floor(Math.random() * 30) + 70
    }));
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Google AI Predictive Modeling</h3>
      </div>

      <div className="space-y-6">
        {/* Available Datasets Section */}
        {availableDatasets.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-2">Load Cleaned Dataset</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {availableDatasets.map((dataset) => (
                <Button
                  key={dataset.id}
                  variant="outline"
                  size="sm"
                  onClick={() => loadDatasetContent(dataset)}
                  className="justify-start truncate"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {dataset.file_name}
                </Button>
              ))}
            </div>
            {selectedDataset && (
              <Badge variant="secondary" className="mt-2">
                Loaded: {selectedDataset}
              </Badge>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2">Industry</label>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger>
              <SelectValue placeholder="Select industry for predictions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="finance">Finance</SelectItem>
              <SelectItem value="ecommerce">E-commerce</SelectItem>
              <SelectItem value="logistics">Logistics</SelectItem>
              <SelectItem value="realestate">Real Estate</SelectItem>
              <SelectItem value="technology">Technology</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Natural Language Query Section */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium mb-2 text-blue-800">Natural Language Queries</h4>
          <div className="flex gap-2">
            <Input
              placeholder="Ask: 'What was the highest selling product in Q1?' or 'Predict customer churn for April'"
              value={naturalLanguageQuery}
              onChange={(e) => setNaturalLanguageQuery(e.target.value)}
              className="flex-1"
            />
            <Button 
              onClick={handleNaturalLanguageQuery}
              disabled={isLoading || !naturalLanguageQuery.trim() || !historicalData.trim()}
              size="sm"
            >
              Ask AI
            </Button>
          </div>
          <div className="text-xs text-blue-600 mt-1">
            Example: "What was the highest selling product?" or "Predict revenue for next quarter"
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Historical Data</label>
          <Textarea
            placeholder="Enter historical data for prediction (CSV format, time series data, etc.) or load from cleaned datasets above"
            value={historicalData}
            onChange={(e) => setHistoricalData(e.target.value)}
            rows={6}
          />
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        <Button 
          onClick={handleGeneratePredictions}
          disabled={isLoading || !industry || !historicalData.trim()}
          className="w-full"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating Predictions...
            </>
          ) : (
            'Generate AI Predictions'
          )}
        </Button>

        {/* Results Display */}
        {predictions.length > 0 && (
          <div className="space-y-4">
            <h4 className="font-medium">AI Analysis Results</h4>
            {predictions.map((prediction) => (
              <div key={prediction.id} className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  {prediction.type === 'query' ? (
                    <MessageSquare className="h-4 w-4 text-blue-500" />
                  ) : (
                    <BarChart3 className="h-4 w-4 text-green-500" />
                  )}
                  <span className="text-sm font-medium">
                    {prediction.type === 'query' ? 'Query Response' : 'Prediction Analysis'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {prediction.timestamp.toLocaleTimeString()}
                  </span>
                </div>
                {prediction.query && (
                  <div className="text-sm font-medium text-blue-700 mb-2">
                    Q: {prediction.query}
                  </div>
                )}
                <div className="whitespace-pre-wrap text-sm">{prediction.result}</div>
              </div>
            ))}
          </div>
        )}

        {/* Visualizations */}
        {visualizations.length > 0 && (
          <div className="space-y-4">
            <h4 className="font-medium">Generated Visualizations</h4>
            {visualizations.map((viz) => (
              <div key={viz.id} className="p-4 border rounded-lg">
                <h5 className="font-medium mb-2">{viz.title}</h5>
                <div className="h-64 bg-muted rounded flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <BarChart3 className="h-8 w-8 mx-auto mb-2" />
                    <p>Visualization: {viz.type}</p>
                    <p className="text-xs">Data points: {viz.data.length}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
